import axios from 'axios';
import { toast } from 'react-toastify';
import { API_CONFIG } from 'api/config';
import auth from 'api/core/auth';
import meApi from 'api/modules/meApi';

class HttpClient {
  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request Interceptor
    this.instance.interceptors.request.use(
      config => {
        if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
          const csrfToken = this.getCookie('csrftoken');
          if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken;
          } else {
            delete config.headers['X-CSRFToken'];
          }
        }
        config.withCredentials = true;
        return config;
      },
      error => Promise.reject(error),
    );

    // Response Interceptor
    this.instance.interceptors.response.use(
      response => {
        if (API_CONFIG.SUCCESS_STATUS_CODES.includes(response.status)) {
          return response.data;
        }
        return response;
      },
      async error => {
        if (error.response) {
          const status = error.response.status;
          const url = error.config?.url || '';

          // 401 → 로그인 필요
          if (status === 401) {
            const url = error.config?.url || '';
            // /me 에 한해서는 재시도 허용
            if (url.includes('/me') && !error.config.__isRetryRequest) {
              error.config.__isRetryRequest = true;
              console.warn('[httpClient] /me 401 detected, retrying once...');
              return this.instance(error.config);
            }
            auth.clearAuth();
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            return Promise.reject(
              new Error(API_CONFIG.ERROR_MESSAGES.UNAUTHORIZED),
            );
          }

          // 403 → CSRF 토큰 갱신 후 재시도, 실패 시 fallback or 로그아웃
          if (status === 403 && !url.includes('/logout/')) {
            // CSRF 토큰 만료일 수 있으므로 갱신 후 재시도
            if (!error.config.__csrfRetry) {
              error.config.__csrfRetry = true;
              console.warn(
                '[httpClient] 403 Forbidden. Attempting to refresh CSRF token...',
              );
              try {
                await this.instance.get('/p/auth/csrf/');
                return this.instance(error.config);
              } catch (csrfError) {
                console.error(
                  '[httpClient] CSRF refresh failed, proceeding to fallback.',
                  csrfError,
                );
              }
            }

            if (error.config.skipGlobal403) {
              return Promise.reject(error);
            }

            console.warn(
              '[httpClient] 403 Forbidden. Attempting to re-fetch /me for fallback.',
            );
            try {
              const me = await meApi.getMe();

              const tenants = me?.tenants || [];
              const warehouses =
                me?.warehouse_permissions?.map(p => p.warehouse) || [];

              if (tenants.length && warehouses.length) {
                const tenant = tenants[0];
                const warehouse = warehouses[0];

                const tenantSlug = tenant.domains?.[0]?.domain;

                const fallbackUrl = `/${tenantSlug}/${warehouse.name}`;

                // fallback 이동
                window.location.href = fallbackUrl;
                return Promise.reject(
                  new Error(API_CONFIG.ERROR_MESSAGES.FORBIDDEN),
                );
              }

              // fallback 불가 → 로그아웃
              console.error(
                '[httpClient] 403 fallback failed. No tenant/warehouse. Logging out.',
              );
              // auth.clearAuth();
              window.location.href = '/login';
              return Promise.reject(
                new Error(API_CONFIG.ERROR_MESSAGES.FORBIDDEN),
              );
            } catch (e) {
              console.error(
                '[httpClient] 403 fallback /me call failed. Logging out.',
                e,
              );
              auth.clearAuth();
              window.location.href = '/login';
              return Promise.reject(
                new Error(API_CONFIG.ERROR_MESSAGES.FORBIDDEN),
              );
            }
          }

          // 기타 에러 핸들링
          switch (status) {
            case 429: {
              const msg =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                API_CONFIG.ERROR_MESSAGES.DUPLICATE_EXPORT;
              toast.warning(msg);
              return Promise.reject(error);
            }
            case 400: {
              const data = error.response?.data;
              const raw =
                typeof data === 'string' ? data : JSON.stringify(data || '');

              if (raw.toLowerCase().includes('blocked during stocktaking')) {
                toast.warning(
                  'Stocktaking in progress — this operation is currently blocked.',
                );
              }
              return Promise.reject(error);
            }
            case 404:
              return Promise.reject(
                new Error(API_CONFIG.ERROR_MESSAGES.NOT_FOUND),
              );
            case 500:
              return Promise.reject(
                new Error(API_CONFIG.ERROR_MESSAGES.SERVER),
              );
            default:
              return Promise.reject(
                new Error(API_CONFIG.ERROR_MESSAGES.DEFAULT),
              );
          }
        }

        if (error.request) {
          return Promise.reject(new Error(API_CONFIG.ERROR_MESSAGES.NETWORK));
        }

        return Promise.reject(error);
      },
    );
  }

  getCookie(name) {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${name}=([^;]*)`),
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  get(url, config = {}) {
    return this.instance.get(url, config);
  }

  post(url, data, config = {}) {
    return this.instance.post(url, data, config);
  }

  put(url, data, config = {}) {
    return this.instance.put(url, data, config);
  }

  patch(url, data, config = {}) {
    return this.instance.patch(url, data, config);
  }

  delete(url, config = {}) {
    return this.instance.delete(url, config);
  }
}

export default new HttpClient();
